import { useEffect, useRef } from 'react'

import { ActiveTab } from '@/constants/chat'
import { UserRole } from '@/constants/room'
import { loadNegotiationMessages } from '@/redux/asyncThunks/negotiationWebsocket'
import { AppDispatch } from '@/redux/store'
import { setActiveTab } from '@/redux/slices/chatSlice'
import { PhaseType } from '@/redux/slices/timerConfigurationSlice'
import { Negotiation } from '@/redux/types/negotiationWebsocket'
import {
  getNegotiationPhaseTargetTab,
  getPhaseTransitionKey,
} from '@/utils/phaseNavigation'

type SidebarPanel = 'game' | 'chat'

type TeamDummyAssignment = {
  botId: number
  botName: string
  teamId: number
  teamName: string
} | null

type UsePhaseNavigationParams = {
  dispatch: AppDispatch
  setSidebarPanel: (panel: SidebarPanel) => void
  userRole?: string
  manualMode: boolean
  isGameStarted: boolean
  phaseId: number | null
  currentRound: number
  currentPhase: PhaseType | null
  isRandRPhase: boolean
  isAdvisorPhase: boolean
  isNegotiationPhase: boolean
  isDecisionPhase: boolean
  teamDummyAssignment: TeamDummyAssignment
  activeNegotiations: Negotiation[]
}

export const usePhaseNavigation = ({
  dispatch,
  setSidebarPanel,
  userRole,
  manualMode,
  isGameStarted,
  phaseId,
  currentRound,
  currentPhase,
  isRandRPhase,
  isAdvisorPhase,
  isNegotiationPhase,
  isDecisionPhase,
  teamDummyAssignment,
  activeNegotiations,
}: UsePhaseNavigationParams) => {
  const prevPhaseKeyRef = useRef<string | null>(null)
  const negotiationNavigatedForPhaseRef = useRef<string | null>(null)
  const decisionNavigatedForPhaseRef = useRef<string | null>(null)

  const phaseKey = getPhaseTransitionKey(
    manualMode,
    phaseId,
    currentRound,
    currentPhase,
    isRandRPhase,
    isAdvisorPhase,
    isNegotiationPhase,
    isDecisionPhase
  )

  const decisionPhaseKey = phaseKey ?? `${currentRound}-decision`

  useEffect(() => {
    if (userRole !== UserRole.Student || !isGameStarted || !phaseKey) return
    if (phaseKey === prevPhaseKeyRef.current) return

    prevPhaseKeyRef.current = phaseKey

    if (!isNegotiationPhase) {
      negotiationNavigatedForPhaseRef.current = null
    }

    if (!isDecisionPhase) {
      decisionNavigatedForPhaseRef.current = null
    }

    setSidebarPanel('chat')

    if (isDecisionPhase && !manualMode) {
      dispatch(setActiveTab(ActiveTab.Decision))
      decisionNavigatedForPhaseRef.current = decisionPhaseKey
      return
    }

    if (isRandRPhase || isAdvisorPhase) {
      dispatch(setActiveTab(ActiveTab.Advisor))
      return
    }

    if (isNegotiationPhase) {
      const targetTab = getNegotiationPhaseTargetTab(
        teamDummyAssignment,
        activeNegotiations
      )

      if (targetTab === ActiveTab.DummyLLMChat) {
        dispatch(setActiveTab(ActiveTab.DummyLLMChat))
        negotiationNavigatedForPhaseRef.current = phaseKey
        return
      }

      if (targetTab) {
        dispatch(setActiveTab(targetTab))
        dispatch(
          loadNegotiationMessages({
            negotiationId: targetTab,
            page: 1,
            pageSize: 20,
          })
        )
        negotiationNavigatedForPhaseRef.current = phaseKey
      }
    }
  }, [
    userRole,
    isGameStarted,
    phaseKey,
    decisionPhaseKey,
    isRandRPhase,
    isAdvisorPhase,
    isNegotiationPhase,
    isDecisionPhase,
    manualMode,
    teamDummyAssignment,
    activeNegotiations,
    dispatch,
    setSidebarPanel,
  ])

  useEffect(() => {
    if (userRole !== UserRole.Student || !isGameStarted) return
    if (!isDecisionPhase || manualMode) return
    if (decisionNavigatedForPhaseRef.current === decisionPhaseKey) return

    setSidebarPanel('chat')
    dispatch(setActiveTab(ActiveTab.Decision))
    decisionNavigatedForPhaseRef.current = decisionPhaseKey
  }, [
    userRole,
    isGameStarted,
    isDecisionPhase,
    manualMode,
    decisionPhaseKey,
    dispatch,
    setSidebarPanel,
  ])

  useEffect(() => {
    if (userRole !== UserRole.Student || !isGameStarted || !phaseKey) return
    if (!isNegotiationPhase) return
    if (negotiationNavigatedForPhaseRef.current === phaseKey) return

    const targetTab = getNegotiationPhaseTargetTab(
      teamDummyAssignment,
      activeNegotiations
    )

    if (!targetTab) return

    setSidebarPanel('chat')

    if (targetTab === ActiveTab.DummyLLMChat) {
      dispatch(setActiveTab(ActiveTab.DummyLLMChat))
    } else {
      dispatch(setActiveTab(targetTab))
      dispatch(
        loadNegotiationMessages({
          negotiationId: targetTab,
          page: 1,
          pageSize: 20,
        })
      )
    }

    negotiationNavigatedForPhaseRef.current = phaseKey
  }, [
    userRole,
    isGameStarted,
    phaseKey,
    isNegotiationPhase,
    teamDummyAssignment,
    activeNegotiations,
    dispatch,
    setSidebarPanel,
  ])
}
